import { getEnvironment } from "../../util/getEnvironment";
import { unreachable } from "../../util/unreachable";

const getEmulatorBaseUrl = (): string => {
  const env = getEnvironment();
  switch (env) {
    case "PRD":
      return "https://emulator.pleevi.ai";
    case "STG":
      return "https://emulator.stg.pleevi.ai";
    case "SBX":
      return "https://emulator.sbx.pleevi.ai";
    case "LOCAL":
      if (window.location.hostname.endsWith("pleevi.localhost")) {
        return `http://${window.location.hostname.replace(/^portal\./, "emulator.")}`;
      }
      return "http://localhost:8000";
    default:
      return unreachable(env);
  }
};

export type EmulatedSite = {
  _id: string;
  site_id: string;
  partner_id: string;
  forecasted_device_config: {
    pv: Record<string, unknown>;
    ucl: Record<string, unknown>;
  };
  ocpp_config: {
    server_url: string;
    socket_to_asset_ids: Record<string, string[]>;
  } | null;
  simulator_state: Record<string, unknown> | null;
  updated_at: string | null;
  paused: boolean;
};

export type PaginatedEmulatedSites = {
  data: EmulatedSite[];
  page_size: number;
  end_cursor?: string | null;
  has_next_page?: boolean;
};

export type CreateEmulatedSiteRequest = {
  site_id: string;
  partner_id: string;
  forecasted_device_config?: {
    pv: Record<string, unknown>;
    ucl: Record<string, unknown>;
  };
  ocpp_config?: {
    server_url: string;
    socket_to_asset_ids: Record<string, string[]>;
  } | null;
};

export type ActiveTransaction = {
  asset_id: string;
  transaction_id: string;
};

export type AddTransactionRequest = {
  asset_id: string;
  transaction_id: string;
  start_energy?: number;
  full_battery_energy: number;
  max_power: number;
  used_charging_pins?: ("pin1" | "pin2" | "pin3")[];
  start_time: string;
  stop_time: string;
  requested_min_energy?: number;
  requested_max_energy?: number;
  estimated_departure_time?: string | null;
  priority?: number;
  badge_id?: string;
};

export const listEmulatedSites = async (params?: {
  after?: string;
  page_size?: number;
}): Promise<PaginatedEmulatedSites> => {
  const baseUrl = getEmulatorBaseUrl();
  const token = localStorage.getItem("token");
  const searchParams = new URLSearchParams();
  if (params?.after) {
    searchParams.set("after", params.after);
  }
  if (params?.page_size) {
    searchParams.set("page_size", String(params.page_size));
  }

  const response = await fetch(
    `${baseUrl}/v1/emulator/sites?${searchParams.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(
      error?.detail?.[0]?.msg ?? `Request failed: ${response.status}`,
    );
  }

  return response.json();
};

export const createEmulatedSite = async (
  body: CreateEmulatedSiteRequest,
): Promise<Record<string, string>> => {
  const baseUrl = getEmulatorBaseUrl();
  const token = localStorage.getItem("token");

  const response = await fetch(`${baseUrl}/v1/emulator/sites`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(
      error?.detail?.[0]?.msg ?? `Request failed: ${response.status}`,
    );
  }

  return response.json();
};

export const getEmulatedSite = async (
  siteId: string,
): Promise<EmulatedSite> => {
  const baseUrl = getEmulatorBaseUrl();
  const token = localStorage.getItem("token");

  const response = await fetch(
    `${baseUrl}/v1/emulator/sites/${encodeURIComponent(siteId)}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(
      error?.detail?.[0]?.msg ?? `Request failed: ${response.status}`,
    );
  }

  return response.json();
};

export const pauseEmulatedSite = async (
  siteId: string,
): Promise<Record<string, string>> => {
  const baseUrl = getEmulatorBaseUrl();
  const token = localStorage.getItem("token");

  const response = await fetch(
    `${baseUrl}/v1/emulator/sites/${encodeURIComponent(siteId)}/pause`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(
      error?.detail?.[0]?.msg ?? `Request failed: ${response.status}`,
    );
  }

  return response.json();
};

export const unpauseEmulatedSite = async (
  siteId: string,
): Promise<Record<string, string>> => {
  const baseUrl = getEmulatorBaseUrl();
  const token = localStorage.getItem("token");

  const response = await fetch(
    `${baseUrl}/v1/emulator/sites/${encodeURIComponent(siteId)}/unpause`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(
      error?.detail?.[0]?.msg ?? `Request failed: ${response.status}`,
    );
  }

  return response.json();
};

export const resetEmulatedSite = async (
  siteId: string,
): Promise<Record<string, string>> => {
  const baseUrl = getEmulatorBaseUrl();
  const token = localStorage.getItem("token");

  const response = await fetch(
    `${baseUrl}/v1/emulator/sites/${encodeURIComponent(siteId)}/reset`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(
      error?.detail?.[0]?.msg ?? `Request failed: ${response.status}`,
    );
  }

  return response.json();
};

export const addEmulatorTransaction = async (
  siteId: string,
  body: AddTransactionRequest,
): Promise<Record<string, string>> => {
  const baseUrl = getEmulatorBaseUrl();
  const token = localStorage.getItem("token");

  const response = await fetch(
    `${baseUrl}/v1/emulator/sites/${encodeURIComponent(siteId)}/transactions`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(
      error?.detail?.[0]?.msg ?? `Request failed: ${response.status}`,
    );
  }

  return response.json();
};

export const getActiveTransactions = async (
  siteId: string,
): Promise<ActiveTransaction[]> => {
  const baseUrl = getEmulatorBaseUrl();
  const token = localStorage.getItem("token");

  const response = await fetch(
    `${baseUrl}/v1/emulator/sites/${encodeURIComponent(siteId)}/transactions/active`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(
      error?.detail?.[0]?.msg ?? `Request failed: ${response.status}`,
    );
  }

  return response.json();
};

export const stopTransaction = async (
  siteId: string,
  transactionId: string,
): Promise<Record<string, string>> => {
  const baseUrl = getEmulatorBaseUrl();
  const token = localStorage.getItem("token");

  const response = await fetch(
    `${baseUrl}/v1/emulator/sites/${encodeURIComponent(siteId)}/transactions/${encodeURIComponent(transactionId)}/stop`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(
      error?.detail?.[0]?.msg ?? `Request failed: ${response.status}`,
    );
  }

  return response.json();
};
